/**
 * PORT (wide screens): the original's lists of rows on a phone - the fight screen's tasks (career ranks, missions,
 * survival, bosses; new_interf init), the online lobby's rooms on it (runtime/online-lobby.ts), the barracks' maps
 * (menu_just_play: new_interf init, an online room's maps: runtime/online-room-maps.ts) and the tasks tab (clip_task).
 * The original put a row every 20 px: on a phone in landscape a line of 7.5 CSS px text and a target 13 CSS px tall
 * (each row's button: its text's width, not the list's). With a wide host where 20 px measure less than ROW_CSS:
 *  - the lists put their rows `listPitch` apart (the code that lays them out asks for it): a row a finger's target
 *    of ROW_CSS, fewer rows in the list's mask, the slider shown where they no longer fit it (the lists' scrolling is
 *    the original's formula of the list's height, which holds for any step);
 *  - each row's button takes the taps of its whole step, the list's width (clear of the slider beside it): no gap
 *    between two rows, no row that answers only on its text;
 *  - the rows' text is one size in a list, about ROW_TEXT_CSS CSS px, smaller where a row's text would not fit its
 *    field's width (every row keeps its whole text).
 * Without a wide host (a desktop) nothing changes: 20 px, the original's rows.
 */
import { Point, Shape, Sprite, TextField, TextFormat } from '@flash';

/** The original's step between two rows of a list (px). */
export const ROW = 20;
/** What a row of a list measures at least with a wide host (CSS px): its whole step a finger's target. */
export const ROW_CSS = 40;
/** A row's text (CSS px) and the largest size it takes (px as the original draws it). */
const ROW_TEXT_CSS = 11.5;
const ROW_TEXT_MAX = 20;
/** Room kept between a row's target and the slider beside the list (CSS px). */
const SLIDER_GAP_CSS = 4;

/**
 * The step between two rows of a list on this stage: ROW without a wide host (or where ROW px measure ROW_CSS CSS px
 * already), else ROW_CSS CSS px; at most what lets `rows` rows fit a list of `window` px that has no slider.
 */
export function listPitch(stage: any, rows = 0, window = 0): number {
  const css = stage && stage.$visibleArea ? Number(stage.cssScale) : 0;
  if (!(css > 0) || ROW * css >= ROW_CSS) return ROW;
  let p = Math.ceil(ROW_CSS / css);
  if (rows > 0 && window > 0) p = Math.min(p, Math.floor(window / rows));
  return Math.max(ROW, p);
}

/**
 * A list of the screens: its container (a child of the screen), its rows' text field and button, and its scrolling
 * (new_interf.slider_proceed: the container at `top` less the slider's share of its height less 20 px and the slider's
 * range, init_slider(`slider`)).
 */
interface ListKind {
  container: string;
  text: string;
  button: string;
  top: number;
  slider: [string, string?];
}

const LISTS: ListKind[] = [
  { container: 'choose_container', text: 'task_name', button: 'choose_task_button', top: 200, slider: ['fight', 'slider'] },
  { container: 'just_play_container', text: 'task_name', button: 'choose_task_button', top: 182, slider: ['just_play'] },
  { container: 'tasks_container', text: 'task_text', button: 'click_task', top: 118, slider: ['tasks'] },
  // (the saved replays: no slider, every row on the page)
  { container: 'note_container', text: 'note_name', button: 'open_note', top: 141, slider: ['replay'] },
];

/** The replays' page without a saved replay says so (the source text; the catalogue translates it). */
export const MSG_NO_NOTES = 'Сохранённых записей пока нет. После боя сохраните его запись значком повтора на главном экране.';

/** The note of an empty page of saved replays (menu_replay's note_container, as wide as its rows): shown or hidden. */
export function emptyNotes(box: any, empty: boolean): void {
  if (!box) return;
  let note = box.getChildByName('notes_empty');
  if (!note && empty) {
    note = new TextField();
    note.name = 'notes_empty';
    // (on a phone the rows' text size: the lists' own layout, growTexts leaves them alone)
    const st = box.stage;
    const css = st && st.$visibleArea ? Number(st.cssScale) : 0;
    const f = new TextFormat('Arial', css > 0 && css < 0.99 ? Math.min(ROW_TEXT_MAX, Math.ceil(ROW_TEXT_CSS / css)) : 12, 0x666666, false, true);
    note.defaultTextFormat = f;
    note.wordWrap = true;
    note.multiline = true;
    note.selectable = false;
    note.mouseEnabled = false;
    note.width = 200;
    note.height = 200;
    note.x = 0;
    note.y = 6;
    note.text = MSG_NO_NOTES;
    box.addChild(note);
  }
  if (note) note.visible = empty;
}

/** What a row had from the game (given back without a wide host) and what was laid out last. */
interface RowState {
  kind: ListKind;
  /** The text field's size, place and box, and the button's hit state, as the game gave them. */
  size: number;
  y: number;
  width: number;
  height: number;
  wordWrap: boolean;
  hit: any;
  key: string;
  /** A transparent box of the row's whole step (its bounds: the list's height counts whole steps from its top). */
  box: any;
  /** The largest size up to the target that its text fits at (at most MAX_LINES lines), for `fitFor` (text, target, width). */
  fit: number;
  fitFor: string;
}

/** Lines a row's text may take (a long rank's or room's name wraps rather than shrinks). */
const MAX_LINES = 2;

/** The lists' containers (growTexts leaves their rows to this). */
export const LIST_CONTAINERS: ReadonlySet<string> = new Set(LISTS.map((l) => l.container));

export class WideLists {
  private readonly rows = new Map<any, RowState>();

  /**
   * @param stage the stage (its cssScale)
   * @param interf the game's interface (new_interf: the sliders' ranges, init_slider), or null
   */
  constructor(
    private readonly stage: any,
    private readonly interf: () => any,
  ) {}

  /**
   * Lays out the rows of the lists of `screens` (the game's screens shown, not the popups) for a wide host (`on`); the
   * rows of lists no longer shown, and every row without a wide host, get back what the game gave them.
   */
  update(screens: any[], on: boolean): void {
    const live = new Set<any>();
    const css = this.stage.cssScale;
    if (on && css > 0) {
      for (const link of screens) {
        for (const kind of LISTS) {
          const box = link && link[kind.container];
          if (!box || !box.stage || typeof box.numChildren !== 'number') continue;
          this.layout(link, box, kind, css, live);
        }
      }
    }
    for (const [row, st] of this.rows) {
      if (live.has(row)) continue;
      this.restore(row, st);
      this.rows.delete(row);
    }
  }

  private layout(link: any, box: any, kind: ListKind, css: number, live: Set<any>): void {
    const rows: any[] = [];
    for (let i = 0; i < box.numChildren; i++) {
      const r = box.getChildAt(i);
      if (r && r.visible && r[kind.text] && r[kind.button]) rows.push(r);
    }
    if (!rows.length) return;
    rows.sort((a, b) => a.y - b.y);
    // The step the list was laid out with (one row: the step of this stage).
    let step = Infinity;
    for (let i = 1; i < rows.length; i++) if (rows[i].y - rows[i - 1].y > 0.5) step = Math.min(step, rows[i].y - rows[i - 1].y);
    if (!Number.isFinite(step)) step = listPitch(this.stage);
    if (step <= ROW + 0.5) return; // (the original's rows: as they are)
    // The list's right edge: the slider beside it (shown or not: the list's mask ends before it), else the field's.
    const slider = link.slider && link.slider.stage ? link.slider.getBounds(this.stage) : null;
    // One size for the rows' text: about ROW_TEXT_CSS, less where a row's text would take more than MAX_LINES lines.
    const target = Math.min(ROW_TEXT_MAX, Math.ceil(ROW_TEXT_CSS / css));
    let size = target;
    const widths = new Map<any, number>();
    for (const r of rows) {
      const st = this.stateOf(r, kind);
      if (!st) continue;
      const tf = r[kind.text];
      let w = st.width;
      if (slider) {
        const left = r.globalToLocal(new Point(slider.x - SLIDER_GAP_CSS / css, 0)).x - tf.x;
        if (left > 40) w = Math.min(w, left);
      }
      widths.set(r, w);
      const k = `${String(tf.text ?? '')}|${target}|${Math.round(w)}`;
      if (st.fitFor !== k) {
        st.fit = this.fits(tf, st, target, w);
        st.fitFor = k;
      }
      size = Math.min(size, st.fit);
    }
    let moved = false;
    for (const r of rows) {
      const st = this.stateOf(r, kind);
      if (!st) continue;
      live.add(r);
      const tf = r[kind.text];
      const b = r[kind.button];
      const w = widths.get(r) ?? st.width;
      const px = Math.max(size, st.size);
      const key = `${String(tf.text ?? '')}|${px}|${step}|${Math.round(w)}|${css}`;
      if (key === st.key && tf.getTextFormat().size === px) continue;
      moved = true;
      this.shape(tf, st, px, w);
      // The row's step from its top (the list's formula of its scrolling puts the first step at the list's top and
      // the last one's text at its bottom): the text in its middle, the target all of it, the list's width.
      tf.height = Math.min(step, Math.ceil(tf.textHeight) + 4);
      tf.y = (step - tf.height) / 2;
      const p0 = b.globalToLocal(r.localToGlobal(new Point(tf.x, 0)));
      const p1 = b.globalToLocal(r.localToGlobal(new Point(tf.x + w, step)));
      const s = new Sprite();
      s.name = '$wide_row_hit';
      s.graphics.beginFill(0, 0);
      s.graphics.drawRect(Math.min(p0.x, p1.x), Math.min(p0.y, p1.y), Math.abs(p1.x - p0.x), Math.abs(p1.y - p0.y));
      s.graphics.endFill();
      b.hitTestState = s;
      // (the list's scrolling reads its height from its top: the whole steps, the first from 0 as the original's text)
      if (!st.box) {
        st.box = new Shape();
        st.box.name = '$wide_row_step';
        r.addChildAt(st.box, 0);
      }
      st.box.graphics.clear();
      st.box.graphics.beginFill(0, 0);
      st.box.graphics.drawRect(tf.x, 0, 1, step);
      st.box.graphics.endFill();
      st.key = key;
    }
    // A list that scrolls: its place again for its new height where the slider is (the game placed it for the
    // original's rows).
    const i = moved && link.slider && link.slider.visible ? this.interf() : null;
    if (i && typeof i.init_slider === 'function') {
      i.init_slider(...kind.slider);
      const range = i.slider_bottom - i.slider_top;
      if (range > 0) {
        const p = Math.max(0, Math.min(1, (link.slider.y - i.slider_top) / range));
        box.y = kind.top - p * Math.max(0, box.height - ROW - range);
      }
    }
  }

  /** The row's state (what the game gave it, taken the first time), or null for a text of several sizes. */
  private stateOf(r: any, kind: ListKind): RowState | null {
    let st = this.rows.get(r);
    if (!st) {
      const tf = r[kind.text];
      const size = tf.getTextFormat().size;
      if (typeof size !== 'number' || !(size > 0)) return null;
      st = { kind, size, y: tf.y, width: tf.width, height: tf.height, wordWrap: !!tf.wordWrap, hit: r[kind.button].hitTestState, key: '', box: null, fit: size, fitFor: '' };
      this.rows.set(r, st);
    }
    return st;
  }

  /** The field at `px`, `width` wide, its text wrapped (a box tall enough for MAX_LINES lines). */
  private shape(tf: any, st: RowState, px: number, width: number): void {
    if (tf.getTextFormat().size !== px) {
      const f = new TextFormat();
      f.size = px;
      tf.setTextFormat(f);
    }
    if (!tf.wordWrap) tf.wordWrap = true;
    if (tf.width !== width) tf.width = width;
    const h = Math.ceil(px * 1.25) * MAX_LINES + 4;
    if (tf.height < h) tf.height = h;
    void st;
  }

  /** The largest size up to `size` at which the field's text takes at most MAX_LINES lines of `width` (at least the game's). */
  private fits(tf: any, st: RowState, size: number, width: number): number {
    const was = { size: tf.getTextFormat().size, ww: tf.wordWrap, w: tf.width, h: tf.height, y: tf.y };
    let px = size;
    for (; px > st.size; px--) {
      this.shape(tf, st, px, width);
      if (tf.numLines <= MAX_LINES && tf.textWidth <= width - 3) break;
    }
    if (typeof was.size === 'number' && tf.getTextFormat().size !== was.size) {
      const f = new TextFormat();
      f.size = was.size;
      tf.setTextFormat(f);
    }
    tf.wordWrap = was.ww;
    tf.width = was.w;
    tf.height = was.h;
    tf.y = was.y;
    return px;
  }

  private restore(r: any, st: RowState): void {
    const kind = st.kind;
    const tf = r[kind.text];
    if (tf) {
      if (tf.getTextFormat().size !== st.size) {
        const f = new TextFormat();
        f.size = st.size;
        tf.setTextFormat(f);
      }
      tf.wordWrap = st.wordWrap;
      tf.width = st.width;
      tf.height = st.height;
      tf.y = st.y;
    }
    const b = r[kind.button];
    if (b) b.hitTestState = st.hit;
    if (st.box && st.box.parent) st.box.parent.removeChild(st.box);
  }
}
